import type { FiuuTopUpState, TopUpReturnHint } from './fiuu-topup.js';
import { toTopUpOutcome } from './fiuu-topup.js';

/**
 * Waiting for a top-up to end (MYP-134): reads it from `get_trade` until the record is paid or
 * handled, the address's hint ends it, the time limit passes, or the caller stops.
 */

/** Five seconds between questions: `get_trade` is not fast, and the bank is not either. */
export const FIUU_TOPUP_POLL_INTERVAL_MS = 5_000;
/** Three minutes: an FPX payment that has not ended by then is the bank's delay. */
export const FIUU_TOPUP_POLL_TIMEOUT_MS = 3 * 60_000;
/**
 * How many answers in a row may lack the top-up before it is called not found. A top-up the app
 * just started is the newest row, so it is there at once; three misses means the address points at
 * a top-up that is not this customer's, or not there at all.
 */
export const FIUU_TOPUP_POLL_MISSES = 3;
/**
 * How many unpaid answers in a row the address's hint (`cancelled` / `failed`) needs before the
 * page believes it. One extra look, an interval later: a payment the vendor is still crediting
 * (its callback a few seconds behind the browser) gets the chance to show as paid first, instead
 * of the customer being told it failed. The hint still never outranks the record.
 */
export const FIUU_TOPUP_HINT_CONFIRMATIONS = 2;

/** Where a poll ended. */
export type FiuuTopUpPollOutcome =
  /** Paid or handled by `get_trade`, or — while still pending — cancelled or failed by the hint. */
  | {
      readonly kind: 'final';
      readonly outcome: 'paid' | 'handled' | TopUpReturnHint;
      readonly state: FiuuTopUpState;
    }
  /** Still pending when time ran out. The bank can outlive the page: check the history later. */
  | { readonly kind: 'timedOut' }
  /** The top-up was not among this customer's, `FIUU_TOPUP_POLL_MISSES` times in a row. */
  | { readonly kind: 'notFound' }
  /** Stopped by the caller (the customer left the page). */
  | { readonly kind: 'stopped' };

export interface FiuuTopUpPollOptions {
  /**
   * One look at `get_trade`: the top-up's state, or undefined when it is not there. Throws when
   * there was no answer at all.
   */
  readonly check: () => Promise<FiuuTopUpState | undefined>;
  /** What the return address said (`cancelled` or `failed`), if anything. */
  readonly hint: TopUpReturnHint | undefined;
  /** Called with every state that came back, final or not — for the page to show the amount. */
  readonly onState?: (state: FiuuTopUpState) => void;
  readonly intervalMs?: number;
  readonly timeoutMs?: number;
  readonly signal?: AbortSignal;
  /** Injected for tests; default `setTimeout` and `Date.now`. */
  readonly sleep?: (ms: number) => Promise<void>;
  readonly now?: () => number;
}

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Asks until there is a final answer. The rules:
 *
 * - paid or handled → stop, final;
 * - pending, with a hint, `FIUU_TOPUP_HINT_CONFIRMATIONS` answers in a row → stop as `cancelled`
 *   or `failed` (the vendor's record of an unpaid top-up stays INVALID for good, so waiting longer
 *   would only end in "still waiting"; the one extra look lets a payment still being credited
 *   show as paid first);
 * - pending → ask again after `intervalMs`;
 * - not there → ask again, and stop as not found after `FIUU_TOPUP_POLL_MISSES` misses in a row;
 * - no answer at all (network, timeout, a broken reply) → ask again; it is **never** a failure,
 *   because a lost request says nothing about the bank;
 * - never past `timeoutMs`, checked before every wait.
 */
export async function pollFiuuTopUp(options: FiuuTopUpPollOptions): Promise<FiuuTopUpPollOutcome> {
  const intervalMs = options.intervalMs ?? FIUU_TOPUP_POLL_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? FIUU_TOPUP_POLL_TIMEOUT_MS;
  const sleep = options.sleep ?? realSleep;
  const now = options.now ?? Date.now;
  const deadline = now() + timeoutMs;
  let misses = 0;
  let unpaidInARow = 0;

  for (;;) {
    if (options.signal?.aborted) return { kind: 'stopped' };

    let answered = true;
    let state: FiuuTopUpState | undefined;
    try {
      state = await options.check();
    } catch {
      answered = false; // No answer at all: ask again.
    }

    if (options.signal?.aborted) return { kind: 'stopped' };

    if (answered && state === undefined) {
      misses += 1;
      if (misses >= FIUU_TOPUP_POLL_MISSES) return { kind: 'notFound' };
    } else if (state !== undefined) {
      misses = 0;
      options.onState?.(state);
      unpaidInARow = state.status === 'pending' ? unpaidInARow + 1 : 0;
      const hint = unpaidInARow >= FIUU_TOPUP_HINT_CONFIRMATIONS ? options.hint : undefined;
      const outcome = toTopUpOutcome(state.status, hint);
      if (outcome !== 'pending') return { kind: 'final', outcome, state };
    }

    if (now() + intervalMs > deadline) return { kind: 'timedOut' };
    await sleep(intervalMs);
  }
}
